"use client";
import { useState, useRef } from "react";

export type AccordionItem = {
  question: string;
  answer: string;
};

type AccordionProps = {
  items: AccordionItem[];
  defaultOpenIndex?: number | null;
};

const Accordion: React.FC<AccordionProps> = ({
  items,
  defaultOpenIndex = null,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpenIndex);

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="flex flex-col gap-5">
      {items?.map((item, index) => (
        <AccordionPanel
          key={index}
          item={item}
          isOpen={openIndex === index}
          onClick={() => toggleIndex(index)}
        />
      ))}
    </div>
  );
};
interface AccordionPanelProps {
  item: AccordionItem;
  isOpen: boolean;
  onClick: () => void;
}

const AccordionPanel: React.FC<AccordionPanelProps> = ({
  item,
  isOpen,
  onClick,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`border bg-no-repeat md:bg-contain bg-cover bg-right transition-all duration-300 ${isOpen ? "border-border md:bg-[url(/images/Accordion-bg.png)] bg-[url(/images/Accordion-bg-mobile.svg)]" : "border-border-light"}`}
    >
      <button
        aria-label="arrow"
        className={`w-full lg:p-6 p-4 text-left flex justify-between items-center cursor-pointer transition-all duration-300 ${isOpen ? "lg:pb-0 pb-0" : ""}`}
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span
          className={`text-subtitle-1 font-bold ${isOpen ? "text-primary-text" : "text-gray-500"}`}
        >
          {item?.question}
        </span>
        <span className="text-gray-500 text-xl font-bold">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 12H18"
              stroke="#464953"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 18V6"
              stroke="#464953"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-all duration-300 origin-center ${isOpen ? "rotate-90" : " rotate-0"}`}
            />
          </svg>
        </span>
      </button>
      <div
        ref={contentRef}
        className={`overflow-hidden transition-all duration-300 lg:px-6 px-4 ${isOpen ? "" : ""}`}
        style={
          isOpen
            ? { maxHeight: contentRef.current?.scrollHeight }
            : { maxHeight: 0 }
        }
      >
        <div className="text-subtitle-2 font-regular text-secondary-text mt-3 pb-6">
          {item?.answer}
        </div>
      </div>
    </div>
  );
};
export default Accordion;
